import React, { useState, useEffect } from "react";
import { claimsPost } from "./claimsReviewApi";
import blinkscribeapi from "../api/blinkscribeapi";
import { savedLocallyNotice } from "./noteVisit";
import redmic from "../assets/audio/redmic.png";

// Billing sanity check (2026-08-26): after the provider reviews a note, check
// each of the day's orders against its LCD covered-diagnosis list and the
// note's documented rationale. Suggested edits are drafted ONLY from facts
// already in the note; the provider explicitly accepts or declines each one.
// Accepting applies the edit through the same category-update endpoint the
// note editor's saves use — nothing in the existing flow changes.
const SanityCheckPanel = ({ note, date, isSigned, showToast, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [decided, setDecided] = useState({}); // orderid -> 'accepted' | 'declined' | 'applying'
  const [pickedDx, setPickedDx] = useState({}); // orderid -> {code, description}
  const [reasonText, setReasonText] = useState({});   // orderid -> provider-stated reason
  const [listening, setListening] = useState(null);   // orderid currently capturing voice
  const [justifying, setJustifying] = useState(null); // orderid awaiting justify call
  const [justifyRes, setJustifyRes] = useState({});   // orderid -> justify response

  // Results belong to ONE note — clear them when the selection changes so a
  // previous patient's check never lingers on another patient's screen.
  useEffect(() => {
    setResults(null);
    setDecided({});
    setPickedDx({});
    setReasonText({});
    setJustifyRes({});
    setLoading(false);
  }, [note?.id]);

  const run = async () => {
    if (!note?.appointmentid || !note?.patientid) {
      showToast("This note has no linked appointment — cannot run the check", "info");
      return;
    }
    setLoading(true);
    setResults(null);
    setDecided({});
    try {
      const res = await claimsPost("analyze", {
        date, patientid: note.patientid, appointmentid: note.appointmentid,
      });
      const body = res?.data || res;
      if (body?.status === 200) setResults(body.results || []);
      else showToast(body?.message || "Sanity check failed", "error");
    } catch (e) {
      showToast(e?.response?.data?.message || "Sanity check failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const accept = async (r, editText) => {
    const text = editText || r.suggested_edit;
    const target = r.target_section === "hpi" ? "hpi" : "assessment_plan";
    const nj = note.note_json || {};
    let newVal;
    if (target === "assessment_plan") {
      const cur = Array.isArray(nj.assessment_plan) ? nj.assessment_plan : nj.assessment_plan ? [nj.assessment_plan] : [];
      newVal = [...cur, text];
    } else {
      newVal = `${nj.hpi || ""}\n\n${text}`.trim();
    }
    setDecided((d) => ({ ...d, [r.orderid]: "applying" }));
    try {
      const res = await blinkscribeapi.put("/v1/recording/category/update", {
        context_dict: { [target]: newVal },
        file_name: note.audiolink,
      });
      setDecided((d) => ({ ...d, [r.orderid]: "accepted" }));
      // A note filed to another day's visit is saved in BlinkScribe only.
      const localOnly = savedLocallyNotice(res);
      showToast(localOnly || "Edit applied to the note", localOnly ? "warning" : "success");
      onApplied && onApplied();
    } catch (e) {
      setDecided((d) => ({ ...d, [r.orderid]: undefined }));
      showToast(e?.response?.data?.message || "Failed to apply edit", "error");
    }
  };

  // Voice capture via the browser's Web Speech API — a one-sentence reason
  // doesn't need the full scribe pipeline.
  const startVoice = (orderid) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast("Voice input isn't supported in this browser — type the reason instead", "info"); return; }
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const t = e.results?.[0]?.[0]?.transcript || "";
      setReasonText((m) => ({ ...m, [orderid]: `${m[orderid] || ""} ${t}`.trim() }));
    };
    rec.onend = () => setListening(null);
    rec.onerror = () => setListening(null);
    setListening(orderid);
    rec.start();
  };

  const checkReason = async (r) => {
    const reason = (reasonText[r.orderid] || "").trim();
    if (reason.length < 3) { showToast("State the clinical reason first", "info"); return; }
    setJustifying(r.orderid);
    setJustifyRes((m) => ({ ...m, [r.orderid]: null }));
    try {
      const res = await claimsPost("justify", {
        ordername: r.ordername, ordertype: r.ordertype || "", cpt: r.cpt || "", reason,
      });
      const body = res?.data || res;
      if (body?.status === 200) setJustifyRes((m) => ({ ...m, [r.orderid]: body }));
      else showToast(body?.message || "Reason check failed", "error");
    } catch (e) {
      showToast(e?.response?.data?.message || "Reason check failed", "error");
    } finally {
      setJustifying(null);
    }
  };

  const Badge = ({ ok, labelTrue, labelFalse }) => (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
      ok ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>
      {ok ? labelTrue : labelFalse}
    </span>
  );

  return (
    <div className="px-5 py-3 border-b bg-[#FFFDF7]">
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-auto">
          Billing Sanity Check
        </span>
        <button onClick={run} disabled={loading}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
            loading ? "border-gray-300 text-gray-400" : "border-[#B45309] text-[#B45309] hover:bg-amber-50"}`}>
          {loading ? "Checking orders vs LCD…" : results ? "Re-run check" : "Check orders vs billing/LCD"}
        </button>
      </div>

      {results && results.length === 0 && (
        <div className="text-xs text-gray-500 mt-2">No billable orders found for this visit.</div>
      )}

      {results && results.map((r) => (
        <div key={r.orderid} className="mt-3 border border-gray-200 rounded-lg p-3 bg-white">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-800 mr-auto">
              {r.ordername}{r.cpt ? <span className="text-gray-400 font-normal"> · CPT {r.cpt}</span> : null}
            </span>
            <Badge ok={!!r.lcd_aligned} labelTrue="LCD aligned" labelFalse="Not LCD aligned" />
            <Badge ok={!!r.documented} labelTrue="Necessity documented" labelFalse="Not documented" />
          </div>
          {r.citation ? (
            <div className="text-xs text-gray-500 italic mt-1">“{r.citation}”</div>
          ) : null}
          {(r.suggested_dx || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mt-2">
              <span className="text-[11px] text-gray-500">Dx pointers:</span>
              {r.suggested_dx.map((c) => (
                <span key={c} className="text-[11px] font-mono bg-[#EEF2FB] text-[#1D2951] px-1.5 py-0.5 rounded">{c}</span>
              ))}
            </div>
          )}
          {r.gap ? <div className="text-xs text-amber-800 mt-2">⚠ {r.gap}</div> : null}
          {r.suggested_edit ? (
            <div className="mt-2 border border-amber-200 bg-amber-50 rounded-lg p-2">
              <div className="text-[11px] font-semibold text-amber-800 mb-1">
                Suggested note edit ({r.target_section === "hpi" ? "HPI" : "Assessment & Plan"})
              </div>
              <div className="text-xs text-gray-800">{r.suggested_edit}</div>
              {r.evidence ? (
                <div className="text-[11px] text-gray-500 italic mt-1">
                  Based on {r.evidence_source === "transcript" ? "the visit transcript"
                    : r.evidence_source === "history" ? "patient history"
                    : r.evidence_source === "ordering_diagnosis" ? "the ordering diagnosis"
                    : "the note"}: “{r.evidence}”
                </div>
              ) : null}
              <div className="flex gap-2 mt-2">
                {decided[r.orderid] === "accepted" ? (
                  <span className="text-xs font-semibold text-green-700">✓ Applied to note</span>
                ) : decided[r.orderid] === "declined" ? (
                  <span className="text-xs text-gray-400">Declined</span>
                ) : isSigned ? (
                  <span className="text-xs text-gray-400">Note is signed — edit via addendum flow</span>
                ) : (
                  <>
                    <button onClick={() => accept(r)} disabled={decided[r.orderid] === "applying"}
                      className="px-3 py-1 rounded-md text-xs font-semibold bg-[#1D2951] text-white disabled:bg-gray-400">
                      {decided[r.orderid] === "applying" ? "Applying…" : "Accept"}
                    </button>
                    <button onClick={() => setDecided((d) => ({ ...d, [r.orderid]: "declined" }))}
                      className="px-3 py-1 rounded-md text-xs font-semibold border border-gray-300 text-gray-600">
                      Decline
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : null}
          {!r.suggested_edit && (r.covered_options || []).length > 0 && (
            <div className="mt-2 border border-amber-200 bg-amber-50 rounded-lg p-2">
              <div className="text-[11px] font-semibold text-amber-800 mb-1">
                No supporting evidence found in the note, visit transcript, or chart history.
                Select the clinical indication for this order (LCD-covered):
              </div>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {r.covered_options.map((opt) => (
                  <button key={opt.code}
                    onClick={() => setPickedDx((m) => ({ ...m, [r.orderid]: opt }))}
                    title={opt.description}
                    className={`text-[11px] font-mono px-2 py-1 rounded border ${
                      pickedDx[r.orderid]?.code === opt.code
                        ? "bg-[#1D2951] text-white border-[#1D2951]"
                        : "bg-white text-[#1D2951] border-gray-300 hover:border-[#1D2951]"}`}>
                    {opt.code}{opt.description ? ` · ${opt.description.slice(0, 34)}` : ""}
                  </button>
                ))}
              </div>
              {pickedDx[r.orderid] && (
                <div className="text-xs text-gray-800 mb-2">
                  Will add: “{`${pickedDx[r.orderid].description || pickedDx[r.orderid].code} (${pickedDx[r.orderid].code}): ${r.ordername} performed for evaluation of ${(pickedDx[r.orderid].description || "this indication").toLowerCase()}.`}”
                </div>
              )}
              <div className="flex gap-2">
                {decided[r.orderid] === "accepted" ? (
                  <span className="text-xs font-semibold text-green-700">✓ Applied to note</span>
                ) : isSigned ? (
                  <span className="text-xs text-gray-400">Note is signed — edit via addendum flow</span>
                ) : (
                  <button disabled={!pickedDx[r.orderid] || decided[r.orderid] === "applying"}
                    onClick={() => {
                      const d = pickedDx[r.orderid];
                      accept({ ...r, target_section: "assessment_plan" },
                        `${d.description || d.code} (${d.code}): ${r.ordername} performed for evaluation of ${(d.description || "this indication").toLowerCase()}.`);
                    }}
                    className="px-3 py-1 rounded-md text-xs font-semibold bg-[#1D2951] text-white disabled:bg-gray-300">
                    {decided[r.orderid] === "applying" ? "Applying…" : "Add to note"}
                  </button>
                )}
              </div>
            </div>
          )}
          {(!r.lcd_aligned || !r.documented) && decided[r.orderid] !== "accepted" && (
            <div className="mt-2 border border-gray-200 rounded-lg p-2 bg-[#FAFBFF]">
              <div className="text-[11px] font-semibold text-gray-600 mb-1">
                Or state the clinical reason for {r.ordername} (voice or text) — it will be matched against the LCD covered list:
              </div>
              <div className="flex gap-2 items-start">
                <textarea rows={2} value={reasonText[r.orderid] || ""}
                  onChange={(e) => setReasonText((m) => ({ ...m, [r.orderid]: e.target.value }))}
                  placeholder="e.g. RUQ pain after meals for 2 months with elevated liver enzymes"
                  className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs" />
                {/* Same mic as the chart's blinkscribe record button */}
                <button onClick={() => startVoice(r.orderid)} title="Speak the reason"
                  className={`w-10 h-10 flex items-center justify-center cursor-pointer ${
                    listening === r.orderid ? "animate-pulse" : "hover:scale-105 transition-transform"}`}>
                  <img src={redmic} className="w-[35px] h-[35px]" alt="Mic" />
                </button>
                <button onClick={() => checkReason(r)} disabled={justifying === r.orderid}
                  className="px-3 py-2 rounded-lg text-xs font-semibold bg-[#1D2951] text-white disabled:bg-gray-400">
                  {justifying === r.orderid ? "Checking…" : "Check reason"}
                </button>
              </div>
              {justifyRes[r.orderid] && (justifyRes[r.orderid].supported ? (
                <div className="mt-2 border border-green-200 bg-green-50 rounded-lg p-2">
                  <div className="text-[11px] font-semibold text-green-800 mb-1">
                    Reason supported — LCD-covered match:
                    {(justifyRes[r.orderid].matched || []).slice(0, 3).map((m) => (
                      <span key={m.code} className="font-mono ml-1">{m.code}</span>
                    ))}
                  </div>
                  <div className="text-xs text-gray-800">{justifyRes[r.orderid].suggested_edit}</div>
                  <div className="flex gap-2 mt-2">
                    {isSigned ? (
                      <span className="text-xs text-gray-400">Note is signed — edit via addendum flow</span>
                    ) : (
                      <button onClick={() => accept({ ...r, target_section: "assessment_plan" }, justifyRes[r.orderid].suggested_edit)}
                        disabled={decided[r.orderid] === "applying"}
                        className="px-3 py-1 rounded-md text-xs font-semibold bg-[#1D2951] text-white disabled:bg-gray-400">
                        {decided[r.orderid] === "applying" ? "Applying…" : "Accept & add to note"}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-2 border border-red-200 bg-red-50 rounded-lg p-2">
                  <div className="text-[11px] font-semibold text-red-800">
                    {justifyRes[r.orderid].clinically_justified
                      ? "Reason not supported by LCD coverage"
                      : "Reason does not clinically justify this order"}
                  </div>
                  <div className="text-xs text-gray-700 mt-1">{justifyRes[r.orderid].message}</div>
                  {(justifyRes[r.orderid].covered_hints || []).length > 0 && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      Covered indications include: {justifyRes[r.orderid].covered_hints.join(", ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SanityCheckPanel;
